import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Clock,
  Search,
  Calendar,
  RefreshCw,
  ChevronDown,
  X,
  User as UserIcon,
  UserCog,
  ShoppingBag,
  Stethoscope,
} from "@/components/icons";

// Map resource_type → display label (Spanish) + accent color
const RESOURCE_LABELS = {
  patient: { l: "Paciente", color: "bg-sky-100 text-sky-800" },
  appointment: { l: "Cita", color: "bg-purple-100 text-purple-800" },
  prescription: { l: "Receta", color: "bg-amber-100 text-amber-800" },
  diagnosis: { l: "Diagnóstico", color: "bg-rose-100 text-rose-800" },
  lab_result: { l: "Lab", color: "bg-indigo-100 text-indigo-800" },
  patient_file: { l: "Archivo", color: "bg-zinc-100 text-zinc-800" },
  payment: { l: "Cobro", color: "bg-blue-50 text-emerald-800" },
  office: { l: "Oficina", color: "bg-stone-100 text-stone-800" },
  user: { l: "Usuario", color: "bg-blue-100 text-blue-800" },
  profile: { l: "Perfil", color: "bg-violet-100 text-violet-800" },
  session: { l: "Sesión", color: "bg-black/10 text-black/80" },
};

// Map action → human label + dot color
const ACTION_LABELS = {
  created: { l: "Creó", dot: "bg-[#01BC8E]" },
  updated: { l: "Editó", dot: "bg-amber-500" },
  deleted: { l: "Eliminó", dot: "bg-red-500" },
  registered: { l: "Se registró", dot: "bg-[#01BC8E]" },
  login: { l: "Inició sesión", dot: "bg-sky-500" },
  blocked: { l: "Bloqueó", dot: "bg-red-500" },
  unblocked: { l: "Desbloqueó", dot: "bg-[#01BC8E]" },
  sent_verification: { l: "Reenvió verificación a", dot: "bg-sky-500" },
  marked_arrived: { l: "Marcó llegada de", dot: "bg-[#01BC8E]" },
  unmarked_arrived: { l: "Quitó llegada de", dot: "bg-amber-500" },
  changed_password: { l: "Cambió su contraseña", dot: "bg-amber-500" },
};

const ROLE_LABELS = {
  admin: "Administrador",
  doctor: "Doctor",
  secretary: "Secretaria",
  pharmacy: "Farmacia",
  system: "Sistema",
};

const ROLE_ICON = {
  admin: UserCog,
  doctor: Stethoscope,
  secretary: UserIcon,
  pharmacy: ShoppingBag,
};

function relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "ahora";
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `hace ${d} d`;
  return new Date(iso).toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtFieldName(f) {
  const map = {
    full_name: "Nombre",
    name: "Nombre",
    document_id: "Documento",
    birth_date: "Fecha de nacimiento",
    gender: "Género",
    phone: "Teléfono",
    email: "Email",
    address: "Dirección",
    blood_type: "Tipo de sangre",
    allergies: "Alergias",
    notes: "Notas",
    photo_url: "Foto",
    signature_url: "Firma",
    clinic_logo_url: "Logo clínica",
    specialty: "Especialidad",
    office_id: "Oficina",
    starts_at: "Fecha/hora",
    duration_min: "Duración",
    reason: "Motivo",
    status: "Estado",
    logo_url: "Logo",
  };
  return map[f] || f;
}

function shortValue(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "string") {
    if (v.startsWith("data:")) return "(imagen)";
    return v.length > 60 ? v.slice(0, 60) + "…" : v;
  }
  return String(v);
}

export default function AdminActivity() {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [facets, setFacets] = useState({
    actors: [],
    resource_types: [],
    actions: [],
  });
  const [filters, setFilters] = useState({
    actor_role: "",
    actor_id: "",
    resource_type: "",
    action: "",
    from_date: "",
    to_date: "",
    q: "",
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    return params.toString();
  }, [filters]);

  const fetchInitial = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/activity${queryString ? "?" + queryString : ""}`,
      );
      setRows(data.rows || []);
      setCursor(data.next_cursor || null);
      setHasMore(!!data.has_more);
    } catch {
      toast.error("No se pudo cargar el historial");
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  useEffect(() => {
    api
      .get("/admin/activity/facets")
      .then((r) => setFacets(r.data))
      .catch(() => {});
  }, []);

  const loadMore = async () => {
    if (!cursor) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams(queryString);
      qs.set("cursor", cursor);
      const { data } = await api.get(`/admin/activity?${qs.toString()}`);
      setRows((prev) => [...prev, ...(data.rows || [])]);
      setCursor(data.next_cursor || null);
      setHasMore(!!data.has_more);
    } catch {
      toast.error("No se pudo cargar más");
    } finally {
      setLoading(false);
    }
  };

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () =>
    setFilters({
      actor_role: "",
      actor_id: "",
      resource_type: "",
      action: "",
      from_date: "",
      to_date: "",
      q: "",
    });
  const anyFilter = Object.values(filters).some((v) => v);

  // Hydrate actor name from facets when filter dropdown selects an id only
  const actorById = useMemo(
    () => Object.fromEntries((facets.actors || []).map((a) => [a.id, a])),
    [facets.actors],
  );

  return (
    <div className="space-y-5 fade-up" data-testid="admin-activity-page">
      <header className="flex items-end gap-4 justify-between flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold text-black tracking-tight flex items-center gap-3">
            <Clock className="w-7 h-7" /> Historial
          </h1>
          <p className="text-sm text-black/60 mt-1 max-w-2xl">
            Bitácora unificada de todas las acciones en la plataforma —
            registros, citas, recetas, ediciones de paciente, cambios de
            perfil, sesiones y más.
          </p>
        </div>
        <button
          onClick={fetchInitial}
          data-testid="activity-refresh"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-black text-white text-sm font-bold hover:bg-black/85 transition"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Actualizar
        </button>
      </header>

      {/* Filters */}
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-5 space-y-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FilterSelect
            label="Rol"
            value={filters.actor_role}
            onChange={(v) => setFilter("actor_role", v)}
            options={[
              { v: "", l: "Todos" },
              { v: "doctor", l: "Doctor" },
              { v: "secretary", l: "Secretaria" },
              { v: "pharmacy", l: "Farmacia" },
              { v: "admin", l: "Administrador" },
            ]}
            testid="filter-role"
          />
          <FilterSelect
            label="Usuario"
            value={filters.actor_id}
            onChange={(v) => setFilter("actor_id", v)}
            options={[
              { v: "", l: "Todos" },
              ...(facets.actors || [])
                .filter(
                  (a) => !filters.actor_role || a.role === filters.actor_role,
                )
                .map((a) => ({ v: a.id, l: a.name || a.email })),
            ]}
            testid="filter-actor"
          />
          <FilterSelect
            label="Recurso"
            value={filters.resource_type}
            onChange={(v) => setFilter("resource_type", v)}
            options={[
              { v: "", l: "Todos" },
              ...(facets.resource_types || []).map((r) => ({
                v: r,
                l: RESOURCE_LABELS[r]?.l || r,
              })),
            ]}
            testid="filter-resource"
          />
          <FilterSelect
            label="Acción"
            value={filters.action}
            onChange={(v) => setFilter("action", v)}
            options={[
              { v: "", l: "Todas" },
              ...(facets.actions || []).map((a) => ({
                v: a,
                l: ACTION_LABELS[a]?.l || a,
              })),
            ]}
            testid="filter-action"
          />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FilterInput
            type="date"
            label="Desde"
            icon={Calendar}
            value={filters.from_date}
            onChange={(v) => setFilter("from_date", v)}
            testid="filter-from"
          />
          <FilterInput
            type="date"
            label="Hasta"
            icon={Calendar}
            value={filters.to_date}
            onChange={(v) => setFilter("to_date", v)}
            testid="filter-to"
          />
          <div className="sm:col-span-2">
            <FilterInput
              label="Buscar"
              icon={Search}
              placeholder="Nombre de usuario o paciente…"
              value={filters.q}
              onChange={(v) => setFilter("q", v)}
              testid="filter-q"
            />
          </div>
        </div>
        {anyFilter && (
          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              data-testid="filter-clear"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-black/55 hover:text-black px-3 py-1.5 rounded-xl hover:bg-white/70 transition"
            >
              <X className="w-3.5 h-3.5" /> Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-5" data-testid="activity-list">
        {loading && rows.length === 0 ? (
          <div className="py-12 text-center text-black/55 text-sm">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-black/55 text-sm">
            <Clock className="w-8 h-8 mx-auto mb-3 text-black/30" />
            No hay actividad que coincida con los filtros.
          </div>
        ) : (
          <ul className="space-y-3">
            {rows.map((r) => (
              <ActivityRow key={r.id} row={r} actorById={actorById} />
            ))}
          </ul>
        )}
        {hasMore && (
          <div className="flex justify-center mt-5">
            <button
              onClick={loadMore}
              disabled={loading}
              data-testid="activity-load-more"
              className="px-5 py-2.5 rounded-xl bg-white border border-black/10 text-black font-bold text-sm hover:bg-black/[0.04] transition disabled:opacity-60"
            >
              {loading ? "Cargando…" : "Cargar más"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-bold text-black/55">
        {label}
      </span>
      <div className="mt-1 relative">
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none bg-white border border-black/10 rounded-2xl px-4 pr-10 py-2.5 text-sm text-black focus:outline-none focus:border-black/40"
        >
          {options.map((o) => (
            <option key={o.v} value={o.v}>
              {o.l}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black/40 pointer-events-none" />
      </div>
    </label>
  );
}

function FilterInput({ label, icon: Icon, value, onChange, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-bold text-black/55">
        {label}
      </span>
      <div className="mt-1 relative">
        {Icon && (
          <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 pointer-events-none" />
        )}
        <input
          data-testid={testid}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full bg-white border border-black/10 rounded-2xl ${Icon ? "pl-11" : "pl-4"} pr-4 py-2.5 text-sm text-black focus:outline-none focus:border-black/40`}
        />
      </div>
    </label>
  );
}

function ActivityRow({ row, actorById }) {
  const action = ACTION_LABELS[row.action] || { l: row.action, dot: "bg-black/40" };
  const resource = RESOURCE_LABELS[row.resource_type] || {
    l: row.resource_type,
    color: "bg-black/10 text-black/80",
  };
  const RoleIcon = ROLE_ICON[row.actor_role] || UserIcon;
  const actor = actorById[row.actor_id];
  const changes = row.changes || {};
  const changeFields = Object.keys(changes);
  return (
    <li
      data-testid={`activity-row-${row.id}`}
      className="bg-white rounded-2xl p-4 flex gap-4 items-start hover:shadow-sm transition"
    >
      <div className="relative flex-shrink-0 mt-1">
        <div className="w-9 h-9 rounded-full bg-black/[0.06] flex items-center justify-center">
          <RoleIcon className="w-4 h-4 text-black/70" />
        </div>
        <span
          className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ${action.dot} border-2 border-white`}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-bold text-black truncate">
            {actor?.name || row.actor_name}
          </span>
          <span className="text-[10px] uppercase tracking-widest font-bold text-black/45">
            {ROLE_LABELS[row.actor_role] || row.actor_role}
          </span>
          <span className="text-black/65">{action.l}</span>
          <span
            className={`inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${resource.color}`}
          >
            {resource.l}
          </span>
          {row.resource_label && (
            <span className="font-semibold text-black truncate max-w-[260px]">
              {row.resource_label}
            </span>
          )}
        </div>
        {changeFields.length > 0 && (
          <ul className="mt-2 space-y-1 text-[12px] text-black/75 bg-black/[0.025] rounded-xl px-3 py-2">
            {changeFields.map((f) => (
              <li key={f} className="flex flex-wrap items-baseline gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-black/45">
                  {fmtFieldName(f)}:
                </span>
                <span className="text-red-700/85 line-through">
                  {shortValue(changes[f].before)}
                </span>
                <span className="text-black/45">→</span>
                <span className="text-[#019A75] font-semibold">
                  {shortValue(changes[f].after)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {row.details && Object.keys(row.details).length > 0 && !changeFields.length && (
          <div className="mt-1.5 text-[12px] text-black/55 flex flex-wrap gap-x-3 gap-y-0.5">
            {Object.entries(row.details).map(([k, v]) => (
              <span key={k}>
                <span className="font-semibold">{fmtFieldName(k)}:</span>{" "}
                {shortValue(v)}
              </span>
            ))}
          </div>
        )}
        <div className="mt-1 text-[11px] text-black/45 flex flex-wrap items-center gap-x-3 gap-y-0.5" title={row.created_at}>
          <span>{relativeTime(row.created_at)}</span>
          {row.client_ip && (
            <span
              className="font-mono text-[10px]"
              title={`IP: ${row.client_ip}`}
              data-testid={`activity-ip-${row.id}`}
            >
              IP {row.client_ip}
            </span>
          )}
          {row.user_agent && (
            <span
              className="truncate max-w-[260px]"
              title={row.user_agent}
              data-testid={`activity-ua-${row.id}`}
            >
              {row.user_agent}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
